import { useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCardType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { getTaskCardFileBuckets } from './helpers/splitTaskFiles';
import TaskCardFilesTab from './tabs/TaskCardFilesTab';
import TaskCardGeneralTab from './tabs/TaskCardGeneralTab';
import TaskCardHistoryTab from './tabs/TaskCardHistoryTab';
import TaskCardResponseTab, { canShowResponseTab } from './tabs/TaskCardResponseTab';
import TaskCardReturnedTab, { canShowReturnedTab } from './tabs/TaskCardReturnedTab';
import TaskCardUsersTab from './tabs/TaskCardUsersTab';
import SolarIcon from 'components/icons/SolarIcon';

export type TaskCardOpenIntent = 'response' | 'reject' | null;

interface TaskCardContentProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onHide: () => void;
    openIntent?: TaskCardOpenIntent;
    onOpenIntentApplied?: () => void;
}

const TaskCardContent = ({
    task,
    onTaskUpdated,
    onHide,
    openIntent = null,
    onOpenIntentApplied,
}: TaskCardContentProps) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();
    const [activeTab, setActiveTab] = useState('general');
    const [showRejectForm, setShowRejectForm] = useState(false);
    const { files } = getTaskCardFileBuckets(task);
    const filesCount = files.length;
    const userId = Number(auth?.user?.ID || 0);
    const responseTab = canShowResponseTab(task, userId);
    const showReturnedTab = canShowReturnedTab(task, userId);

    useEffect(() => {
        if (!openIntent) return;

        if (openIntent === 'response') {
            if (responseTab.show) setActiveTab('response');
            setShowRejectForm(false);
        } else if (openIntent === 'reject') {
            setActiveTab('general');
            setShowRejectForm(true);
        }

        onOpenIntentApplied?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apply once per intent+task
    }, [task.ID, openIntent, responseTab.show]);

    useEffect(() => {
        if (!responseTab.show && activeTab === 'response') {
            setActiveTab('general');
        }
        if (!showReturnedTab && activeTab === 'returned') {
            setActiveTab('general');
        }
    }, [responseTab.show, showReturnedTab, activeTab]);

    return (
        <div className="task-card-body d-flex flex-column h-100">
            <div className="task-card-content px-3 pb-4">
                <Tabs
                    variant="underline"
                    activeKey={activeTab}
                    onSelect={(tab) => tab && setActiveTab(tab)}
                    className="mb-3 invoice-tab border-bottom"
                >
                    <Tab
                        eventKey="general"
                        title={
                            <>
                                <SolarIcon name="gear" className="me-2 fs-14" />
                                {t('tabGeneral')}
                            </>
                        }
                    >
                        <TaskCardGeneralTab 
                            task={task}
                            onTaskUpdated={onTaskUpdated}
                            onRequestResponse={() => setActiveTab('response')}
                            showRejectForm={showRejectForm}
                            onShowRejectFormChange={setShowRejectForm}
                        />
                    </Tab>

                    <Tab
                        eventKey="users"
                        title={
                            <>
                                <SolarIcon name="people" className="me-2 fs-14" />
                                {t('tabUsers')}
                            </>
                        }
                    >
                        <TaskCardUsersTab task={task} onTaskUpdated={onTaskUpdated} />
                    </Tab>

                    <Tab
                        eventKey="files"
                        title={
                            <>
                                <SolarIcon name="paperclip" className="me-2 fs-14" />
                                {t('tabFiles')}
                                {filesCount > 0 ? ` (${filesCount})` : ''}
                            </>
                        }
                    >
                        <TaskCardFilesTab task={task} onTaskUpdated={onTaskUpdated} />
                    </Tab>

                    <Tab
                        eventKey="history"
                        title={
                            <>
                                <SolarIcon name="clock-history" className="me-2 fs-14" />
                                {t('tabHistory')}
                            </>
                        }
                    >
                        <TaskCardHistoryTab task={task} />
                    </Tab>

                    {responseTab.show && (
                        <Tab
                            eventKey="response"
                            title={
                                <>
                                    <SolarIcon name="chat-left-text" className="me-2 fs-14" />
                                    {t('tabResponse')}
                                </>
                            }
                        >
                            <TaskCardResponseTab
                                task={task}
                                onTaskUpdated={onTaskUpdated}
                                onAnswerSubmitted={onHide}
                            />
                        </Tab>
                    )}

                    {showReturnedTab && (
                        <Tab
                            eventKey="returned"
                            title={
                                <>
                                    <SolarIcon name="arrow-return-left" className="me-2 fs-14" />
                                    {t('tabReturned')}
                                </>
                            }
                        >
                            <TaskCardReturnedTab task={task} />
                        </Tab>
                    )}
                </Tabs>
            </div>
        </div>
    );
};

export default TaskCardContent;
